import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import { couponAPI } from '../../services/api';
import '../../styles/admin-coupons.css';

const initialFormState = {
    code: '',
    title: '',
    description: '',
    amount: '',
    min_booking_amount: '',
    usage_limit: '',
    valid_from: '',
    valid_until: '',
    status: 'active'
};

const AdminCoupons = () => {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [filters, setFilters] = useState({ search: '', status: 'all' });
    const [formData, setFormData] = useState(initialFormState);

    const loadCoupons = useCallback(async () => {
        try {
            setLoading(true);
            const response = await couponAPI.getAllCoupons();
            setCoupons(response.data?.data || []);
        } catch (error) {
            console.error('Load coupons error:', error);
            alert(error.response?.data?.message || 'Failed to load coupons');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadCoupons();
    }, [loadCoupons]);

    const filteredCoupons = useMemo(() => {
        const query = filters.search.trim().toLowerCase();
        return coupons.filter((coupon) => {
            const statusMatch = filters.status === 'all' || coupon.status === filters.status;
            const searchMatch = !query ||
                coupon.code?.toLowerCase().includes(query) ||
                coupon.title?.toLowerCase().includes(query);
            return statusMatch && searchMatch;
        });
    }, [coupons, filters]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: name === 'code' ? value.toUpperCase() : value
        }));
    };

    const resetForm = () => {
        setEditingId(null);
        setFormData(initialFormState);
    };

    const handleEdit = (coupon) => {
        setEditingId(coupon.id);
        setFormData({
            code: coupon.code || '',
            title: coupon.title || '',
            description: coupon.description || '',
            amount: coupon.amount || '',
            min_booking_amount: coupon.min_booking_amount || '',
            usage_limit: coupon.usage_limit || '',
            valid_from: coupon.valid_from ? String(coupon.valid_from).slice(0, 10) : '',
            valid_until: coupon.valid_until ? String(coupon.valid_until).slice(0, 10) : '',
            status: coupon.status || 'active'
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.code || !formData.title || !formData.amount) {
            alert('Code, title and amount are required.');
            return;
        }

        try {
            setSaving(true);
            const payload = {
                ...formData,
                amount: Number(formData.amount),
                min_booking_amount: Number(formData.min_booking_amount || 0),
                usage_limit: formData.usage_limit ? Number(formData.usage_limit) : null
            };

            if (editingId) {
                await couponAPI.updateCoupon(editingId, payload);
            } else {
                await couponAPI.createCoupon(payload);
            }

            resetForm();
            await loadCoupons();
        } catch (error) {
            console.error('Save coupon error:', error);
            alert(error.response?.data?.message || 'Failed to save coupon');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (coupon) => {
        if (!window.confirm(`Delete coupon ${coupon.code}?`)) return;

        try {
            await couponAPI.deleteCoupon(coupon.id);
            await loadCoupons();
        } catch (error) {
            console.error('Delete coupon error:', error);
            alert(error.response?.data?.message || 'Failed to delete coupon');
        }
    };

    return (
        <div className="admin-layout">
            <AdminSidebar />
            <div className="admin-main coupon-admin-page">
                <AdminHeader title="Coupons" />

                <section className="coupon-hero">
                    <div>
                        <span className="coupon-eyebrow">Discount Engine</span>
                        <h2>Manage amount-based coupon codes</h2>
                        <p>Create fixed-amount discounts, control minimum booking rules, and manage coupon validity from one place.</p>
                    </div>
                </section>
{/* 
                <div className="stats-grid">
                    <div className="stat-card coupon-stat-card">
                        <div className="stat-icon"><i className="fas fa-ticket-alt"></i></div>
                        <div className="stat-details"><span className="stat-label">Total Coupons</span><span className="stat-value">{couponStats.total}</span></div>
                    </div>
                    <div className="stat-card coupon-stat-card active">
                        <div className="stat-icon"><i className="fas fa-check-circle"></i></div>
                        <div className="stat-details"><span className="stat-label">Active</span><span className="stat-value">{couponStats.active}</span></div>
                    </div>
                    <div className="stat-card coupon-stat-card used">
                        <div className="stat-icon"><i className="fas fa-bolt"></i></div>
                        <div className="stat-details"><span className="stat-label">Total Uses</span><span className="stat-value">{couponStats.used}</span></div>
                    </div>
                </div> */}

                <div className="coupon-grid">
                    <div className="coupon-form-card">
                        <div className="filters-header">
                            <h3>{editingId ? 'Edit Coupon' : 'Create Coupon'}</h3>
                        </div>
                        <form className="coupon-form" onSubmit={handleSubmit}>
                            <div className="filters-grid">
                                <div className="filter-group">
                                    <label>Coupon Code</label>
                                    <input name="code" value={formData.code} onChange={handleFormChange} placeholder="SAVE500" />
                                </div>
                                <div className="filter-group">
                                    <label>Title</label>
                                    <input name="title" value={formData.title} onChange={handleFormChange} placeholder="Flat 500 Off" />
                                </div>
                                <div className="filter-group full">
                                    <label>Description</label>
                                    <textarea name="description" rows="3" value={formData.description} onChange={handleFormChange} placeholder="Coupon details for the team and admin." />
                                </div>
                                <div className="filter-group">
                                    <label>Discount Amount</label>
                                    <input type="number" name="amount" value={formData.amount} onChange={handleFormChange} placeholder="500" />
                                </div>
                                <div className="filter-group">
                                    <label>Minimum Booking</label>
                                    <input type="number" name="min_booking_amount" value={formData.min_booking_amount} onChange={handleFormChange} placeholder="5000" />
                                </div>
                                <div className="filter-group">
                                    <label>Usage Limit</label>
                                    <input type="number" name="usage_limit" value={formData.usage_limit} onChange={handleFormChange} placeholder="Leave blank for unlimited" />
                                </div>
                                <div className="filter-group">
                                    <label>Status</label>
                                    <select name="status" value={formData.status} onChange={handleFormChange}>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Valid From</label>
                                    <input type="date" name="valid_from" value={formData.valid_from} onChange={handleFormChange} />
                                </div>
                                <div className="filter-group">
                                    <label>Valid Until</label>
                                    <input type="date" name="valid_until" value={formData.valid_until} onChange={handleFormChange} />
                                </div>
                            </div>

                            <div className="coupon-form-actions">
                                {editingId && (
                                    <button type="button" className="toolbar-refresh" onClick={resetForm}>
                                        Cancel Edit
                                    </button>
                                )}
                                <button type="submit" className="toolbar-refresh primary" disabled={saving}>
                                    {saving ? 'Saving...' : editingId ? 'Update Coupon' : 'Create Coupon'}
                                </button>
                            </div>
                        </form>
                    </div>

                    <div className="filters-card">
                        <div className="filters-header">
                            <h3><i className="fas fa-filter"></i> Filter Coupons</h3>
                        </div>
                        <div className="filters-body">
                            <div className="filters-grid">
                                <div className="filter-group">
                                    <label>Search</label>
                                    <input name="search" value={filters.search} onChange={handleFilterChange} placeholder="Search by code or title" />
                                </div>
                                <div className="filter-group">
                                    <label>Status</label>
                                    <select name="status" value={filters.status} onChange={handleFilterChange}>
                                        <option value="all">All</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="content-header newsletter-content-header">
                    <div className="results-info">
                        <i className="fas fa-ticket-alt"></i>
                        <span>Showing <strong>{filteredCoupons.length}</strong> of <strong>{coupons.length}</strong> coupons</span>
                    </div>
                    <button className="toolbar-refresh" type="button" onClick={loadCoupons}>Refresh</button>
                </div>

                <div className="admin-content">
                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading coupons...</p>
                        </div>
                    ) : (
                        <div className="table-container">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Code</th>
                                        <th>Amount</th>
                                        <th>Min Booking</th>
                                        <th>Usage</th>
                                        <th>Validity</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCoupons.length > 0 ? filteredCoupons.map((coupon) => (
                                        <tr key={coupon.id}>
                                            <td>
                                                <div className="coupon-code-cell">
                                                    <strong>{coupon.code}</strong>
                                                    <span>{coupon.title}</span>
                                                </div>
                                            </td>
                                            <td>₹{Number(coupon.amount || 0).toLocaleString('en-IN')}</td>
                                            <td>₹{Number(coupon.min_booking_amount || 0).toLocaleString('en-IN')}</td>
                                            <td>{Number(coupon.used_count || 0)}{coupon.usage_limit ? ` / ${coupon.usage_limit}` : ''}</td>
                                            <td>{coupon.valid_from ? String(coupon.valid_from).slice(0, 10) : 'Anytime'} to {coupon.valid_until ? String(coupon.valid_until).slice(0, 10) : 'Open'}</td>
                                            <td><span className={`status-badge ${coupon.status}`}>{coupon.status}</span></td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button className="action-btn" type="button" onClick={() => handleEdit(coupon)}>
                                                        <i className="fas fa-pen"></i>
                                                    </button>
                                                    <button className="action-btn delete" type="button" onClick={() => handleDelete(coupon)}>
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="7" className="empty-cell">No coupons found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminCoupons;
